import _ from 'lodash';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import {
    hasSubmitFailed,
    isValid,
    reduxForm,
    SubmissionError,
    InjectedFormProps
} from 'redux-form';
import { getMaxFlexiLoanAmount } from '@api/lendingAPI/ProductOfferAPIHelper';
import { FormField } from './tempFormFields/FormField.js';
import { Select } from './tempFormFields/Select.js';
import { numbersOnly } from '@forms/helpers/normalisers';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { intlFormatCurrency } from '@iwIntlProvider/FormattedCurrency';
import { getCurrentLocation } from '@iwRouter/HistoryProvider';
import {
    FLEXI_LOAN_PRODUCT_TYPE,
    REVENUE_BASED_LOAN_PRODUCT_TYPE
} from '../../multiLoanDashboard/accountsSelector';
import * as multiLoanselectors from '../../multiLoanDashboard/multiLoanDashboardSelector';
import { MainSectionWrapper } from '../../pageStructure/mainSectionWrapper/index';
import * as selectors from '../applyFlexiLoanSelectors';
import {
    APPLY_FLEXI_LOAN_SUBMIT_APPLICATION,
    CREATE_APPLICATION,
    APPLY_REVENUE_BASED_LOAN_SUBMIT_APPLICATION
} from '../ApplyFlexiLoanView.actions';
import applyFlexiLoanFormValidation from './ApplyFlexiLoanFormValidation';
import styles from './ApplyFlexiLoanFormView.module.css';
import messages from './ApplyFlexiLoanFormView.messages';
import ApprovalRequirements from './ApprovalRequirements/ApprovalRequirements';
import loanDurationOptionsDE from './loanDurationOptionsDE.json';
import loanPurposeOptions from './loanPurposeOptions.json';
import loanPurposeOptionsDE from './loanPurposeOptionsDE.json';
import { getQueryParameter } from '@iwRouter/LocationHelper';
import { useEffect } from 'react';
import { tracking } from '@iwoca/frontend-tracking-library';
import { Button, Card, Icon, Spinner } from '@iwoca/orion';
import NewDocUploads from './NewDocUploads/NewDocUploads';
import { useSplitTests } from '../../../api/lendingAPI/lapiHooks';
import { RootState } from '@iwProvider/configureStore';
import { Dispatch } from 'redux';

import { NEW_IwLink } from '../../../iwRouter/NEW_IwLink';

export const MINIMUM_FLEXI_LOAN_AMOUNT = 1000;

// Todo: Make this value dynamic based on BE. Value is currently not exposed via the LAPI
const MAX_REQUEST_AMOUNT = 500000;

export type TFormData = {
    amount: number;
    purpose: string;
    duration: string;
    turnover: string;
};

const formConfiguration = {
    form: 'ApplyForm',
    enableReinitialize: true,
    touchOnChange: true,
    // Typing props as 'any' here as in theory it should be type TApplyFlexiLoanFormViewProps which is declared further down, but it causes an error related to referencing itself
    validate: (values: TFormData, props: any) => {
        return applyFlexiLoanFormValidation({
            values,
            intl: props.intl,
            maxFlexiLoanAmount: props.maxFlexiLoanAmount
        });
    }
};

const mapStateToProps = (state: RootState) => {
    const hasRBLApplication = multiLoanselectors.hasOpenApplication(
        state,
        REVENUE_BASED_LOAN_PRODUCT_TYPE
    );

    const flexiAccounts = _.filter(
        state.api.accounts?.data?.accounts,
        (account) => account.product_type !== 'merchant_cash_advance'
    );

    const ongoingFlexiLoan = _.find(
        flexiAccounts,
        (account) => account.loan_status === 'ongoing'
    );

    const endedFlexiLoan = _.find(
        flexiAccounts,
        (account) => account.loan_status === 'ended'
    );

    const currentPrincipal = ongoingFlexiLoan?.balance?.principal;
    const amount = multiLoanselectors.getApplicationRequestedAmount(state);
    const amountMinusOustandingPrincipal = amount - currentPrincipal || amount;

    // Prevents bug by ensuring prefilled amount does not include outstanding principal, and thus creating a loop where amount is prefilled with requested amount + outstanding principal (called amount on endpoint response)
    const initialAmount =
        state?.api?.application?.data?.requested_products?.[0]
            ?.requested_cash_amount || amountMinusOustandingPrincipal;

    return {
        submitFailed: hasSubmitFailed(formConfiguration.form)(state),
        formHasNoValidationErrors: isValid(formConfiguration.form),
        createApplicationLoading:
            state?.applyFlexiLoanView?.createApplicationLoading!,
        flexiLoanDocumentRequirements:
            multiLoanselectors.getDocumentRequirements(
                state,
                FLEXI_LOAN_PRODUCT_TYPE
            ),
        revenueBasedLoanDocumentRequirements:
            multiLoanselectors.getDocumentRequirements(
                state,
                REVENUE_BASED_LOAN_PRODUCT_TYPE
            ),
        isBankStatementsRequired:
            multiLoanselectors.isFlexiLoanBankStatementRequired(state),
        hasCreatedFlexiLoanApplicationInAccountPages:
            selectors.hasCreatedFlexiLoanApplicationInAccountPages(state),
        hasCreatedRevenueBasedLoanApplicationInAccountPages:
            selectors.hasCreatedRevenueBasedLoanApplicationInAccountPages(
                state
            ),
        hasRblOpenApplication: hasRBLApplication,
        maxFlexiLoanAmount: getMaxFlexiLoanAmount(state),
        initialValues: {
            amount: initialAmount,
            purpose: multiLoanselectors.getApplicationPurpose(state),
            turnover: selectors.getCompanyAnnualTurnover(state),
            // Converted to string as numbers won't work correctly with <Select />
            duration:
                multiLoanselectors.getRequestedDuration(state) &&
                multiLoanselectors.getRequestedDuration(state).toString()
        },
        formAmountValue: state?.form?.ApplyForm?.values?.amount,
        currentCreditLimit: ongoingFlexiLoan?.max_approved_loan_amount,
        currentPrincipal: currentPrincipal,
        isFlexiLoan:
            ongoingFlexiLoan?.product_type === 'credit_facility' ||
            ongoingFlexiLoan?.product_type === 'flexi24',
        formStep: state.applyFlexiLoanView.formStep,
        applicationId: state?.api?.application?.data?.application_id,
        ready: state.applyFlexiLoanView.ready,
        isRepeatCustomer: !!endedFlexiLoan || !!ongoingFlexiLoan,
        isEditingAmount: state.applyFlexiLoanView.isEditingAmount
    };
};

export type TDocumentRequirements = ReturnType<
    typeof multiLoanselectors.getDocumentRequirements
>;

const mapDispatchToProps = (dispatch: Dispatch) => ({
    createApplication: () =>
        dispatch({
            type: CREATE_APPLICATION
        }),
    submitFlexiLoanApplication: () =>
        dispatch({
            type: APPLY_FLEXI_LOAN_SUBMIT_APPLICATION
        }),
    submitRblApplication: () =>
        dispatch({
            type: APPLY_REVENUE_BASED_LOAN_SUBMIT_APPLICATION
        })
});

const checkNonReduxFormValidationAndSubmit = (
    bankStatementRequired: boolean,
    docRequirements: TDocumentRequirements,
    submitApplication: () => void,
    formHasNoValidationErrors: (state: object) => boolean
) => {
    if (bankStatementRequired) {
        throw new SubmissionError({
            error: 'isMissingBankStatementRequired'
        });
    }

    _.forEach(docRequirements, ({ status, hasError, type }) => {
        if (status === 'required' || hasError) {
            throw new SubmissionError({
                error: 'isMissingDocumentRequirements',
                message: `${type} is missing`
            });
        }
    });

    !!formHasNoValidationErrors && submitApplication();
};

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

type TApplyFlexiLoanFormViewProps = PropsFromRedux & InjectedFormProps;

const ApplyFlexiLoanFormView = ({
    handleSubmit,
    submitFlexiLoanApplication,
    submitRblApplication,
    formHasNoValidationErrors,
    submitFailed,
    isBankStatementsRequired,
    flexiLoanDocumentRequirements,
    revenueBasedLoanDocumentRequirements,
    createApplication,
    hasCreatedRevenueBasedLoanApplicationInAccountPages,
    createApplicationLoading,
    hasCreatedFlexiLoanApplicationInAccountPages,
    hasRblOpenApplication,
    maxFlexiLoanAmount,
    formAmountValue,
    currentCreditLimit,
    currentPrincipal,
    isFlexiLoan,
    formStep,
    applicationId,
    ready,
    isRepeatCustomer,
    isEditingAmount
}: TApplyFlexiLoanFormViewProps) => {
    return (
        <MainSectionWrapper className={styles.wrapper}>
            {!ready ? (
                <div className={styles.loadingSpinner}>
                    <Spinner />
                </div>
            ) : (
                <div data-testid="apply-flexi-loan-view">
                    <form
                        onSubmit={handleSubmit(() => {
                            if (
                                (!hasCreatedFlexiLoanApplicationInAccountPages &&
                                    !hasRblOpenApplication) ||
                                isEditingAmount
                            ) {
                                return createApplication();
                            }

                            if (
                                hasCreatedRevenueBasedLoanApplicationInAccountPages &&
                                hasRblOpenApplication
                            ) {
                                return checkNonReduxFormValidationAndSubmit(
                                    isBankStatementsRequired,
                                    revenueBasedLoanDocumentRequirements,
                                    submitRblApplication,
                                    formHasNoValidationErrors
                                );
                            } else {
                                return checkNonReduxFormValidationAndSubmit(
                                    isBankStatementsRequired,
                                    flexiLoanDocumentRequirements,
                                    submitFlexiLoanApplication,
                                    formHasNoValidationErrors
                                );
                            }
                        })}
                    >
                        {formStep === 'createApplication' ? (
                            <CreateApplicationStep
                                maxFlexiLoanAmount={maxFlexiLoanAmount}
                                isFlexiLoan={isFlexiLoan}
                                formAmountValue={formAmountValue}
                                currentPrincipal={currentPrincipal}
                                currentCreditLimit={currentCreditLimit}
                                createApplicationLoading={
                                    createApplicationLoading!
                                }
                            />
                        ) : (
                            <UploadDocumentsStep
                                submitFailed={submitFailed}
                                flexiLoanDocumentRequirements={
                                    flexiLoanDocumentRequirements
                                }
                                applicationId={applicationId}
                                isRepeatCustomer={isRepeatCustomer}
                            />
                        )}
                    </form>
                </div>
            )}
        </MainSectionWrapper>
    );
};

// eslint-disable-next-line import/no-default-export
export default injectIntl(
    // @ts-expect-error injectIntl expects child to use intl prop directly reduxForm does not do this
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(reduxForm(formConfiguration)(ApplyFlexiLoanFormView))
);

const CreateApplicationStep = ({
    maxFlexiLoanAmount,
    isFlexiLoan,
    formAmountValue,
    currentPrincipal,
    currentCreditLimit,
    createApplicationLoading
}: {
    maxFlexiLoanAmount: number;
    isFlexiLoan: boolean;
    formAmountValue: number;
    currentPrincipal: number;
    currentCreditLimit: number;
    createApplicationLoading: boolean;
}) => {
    const intl = useIntl();

    const isTopup = getQueryParameter(getCurrentLocation(), 'topup');

    useEffect(() => {
        if (isTopup) {
            tracking.ampli.topupReapplyFormApplicationStepViewed();
        } else {
            tracking.ampli.newLoanApplicationStepViewed();
        }
    }, [isTopup]);

    return (
        <>
            <NEW_IwLink
                className={styles.backBtn}
                to={{
                    pathname: '/'
                }}
            >
                <Icon icon="angleLeft" />
                <FormattedMessage {...messages.backButton} />
            </NEW_IwLink>

            <Card className={styles.formContainer}>
                <h1 className={styles.title} data-testid="reapply-loan-title">
                    {isTopup ? (
                        <FormattedMessage {...messages.topupLoanTitle} />
                    ) : (
                        <FormattedMessage {...messages.reapplyLoanTitle} />
                    )}
                </h1>

                <FormField
                    className={styles.formFieldWrapper}
                    name="amount"
                    label={
                        isTopup ? (
                            <FormattedMessage
                                {...messages.topupRequestedAmountLabel}
                            />
                        ) : (
                            <FormattedMessage
                                {...messages.requestedAmountLabel}
                            />
                        )
                    }
                    normalize={(value: string) =>
                        parseInt(numbersOnly(value), 10)
                    }
                    format={(value: number) =>
                        value
                            ? intlFormatCurrency({
                                  intl,
                                  value
                              })
                            : ''
                    }
                    placeholder={intl.formatMessage(
                        messages.requestedAmountPlaceholder,
                        {
                            minAmount: intlFormatCurrency({
                                intl,
                                value: MINIMUM_FLEXI_LOAN_AMOUNT
                            }),
                            maxAmount: intlFormatCurrency({
                                intl,
                                value: maxFlexiLoanAmount
                            })
                        }
                    )}
                />

                {isFlexiLoan &&
                    isTopup &&
                    !!formAmountValue &&
                    formAmountValue <= MAX_REQUEST_AMOUNT - currentPrincipal &&
                    // If new credit limit (inputted amount + currentPrincipal) is more than current
                    // credit limit show new credit limit message, else show current credit limit message
                    (formAmountValue + currentPrincipal > currentCreditLimit ? (
                        <p className={styles.newCreditLimit}>
                            <FormattedMessage {...messages.newCreditLimit} />{' '}
                            <strong>
                                {intlFormatCurrency({
                                    intl,
                                    value: formAmountValue + currentPrincipal
                                })}
                            </strong>
                        </p>
                    ) : (
                        <p className={styles.newCreditLimit}>
                            <FormattedMessage
                                {...messages.unchangedCreditLimit}
                            />{' '}
                            <strong>
                                {intlFormatCurrency({
                                    intl,
                                    value: currentCreditLimit
                                })}
                            </strong>
                        </p>
                    ))}

                <Select
                    className={styles.formFieldWrapper}
                    name="purpose"
                    data-testid="flexi-loan-purpose"
                    label={
                        isTopup ? (
                            <FormattedMessage
                                {...messages.topupLoanPurposeLabel}
                            />
                        ) : (
                            <FormattedMessage {...messages.loanPurposeLabel} />
                        )
                    }
                    placeholder={
                        <FormattedMessage
                            {...messages.loanPurposePlaceholder}
                        />
                    }
                    options={
                        getCountry() === 'DE'
                            ? loanPurposeOptionsDE
                            : loanPurposeOptions
                    }
                />

                <FormField
                    className={styles.formFieldWrapper}
                    name="turnover"
                    label={<FormattedMessage {...messages.turnoverLabel} />}
                    normalize={(value: string) =>
                        parseInt(numbersOnly(value), 10)
                    }
                    format={(value: number) =>
                        value
                            ? intlFormatCurrency({
                                  intl,
                                  value
                              })
                            : ''
                    }
                    placeholder={`${intl.formatMessage(
                        messages.turnoverPlaceholder
                    )} - ${intl.formatNumber(100000)}`}
                />

                {getCountry() === 'DE' && (
                    <Select
                        className={styles.formFieldWrapper}
                        name="duration"
                        label={
                            <FormattedMessage {...messages.loanDurationLabel} />
                        }
                        data-testid="flexi-loan-duration"
                        placeholder={
                            <FormattedMessage
                                {...messages.loanDurationPlaceholder}
                            />
                        }
                        options={loanDurationOptionsDE}
                    />
                )}

                <Button
                    className={styles.submitBtn}
                    loading={createApplicationLoading}
                    type="submit"
                >
                    <FormattedMessage {...messages.nextCTA} />
                </Button>
            </Card>
        </>
    );
};

const UploadDocumentsStep = ({
    submitFailed,
    flexiLoanDocumentRequirements,
    applicationId,
    isRepeatCustomer
}: {
    submitFailed: boolean;
    applicationId: string;
    isRepeatCustomer: boolean;
    flexiLoanDocumentRequirements: TDocumentRequirements;
}) => {
    const isTopup = getQueryParameter(getCurrentLocation(), 'topup');

    useEffect(() => {
        if (isTopup) {
            tracking.ampli.topupReapplyFormDocumentsStepViewed({
                application_id: applicationId
            });
        } else {
            tracking.ampli.newLoanDocumentsStepViewed({
                application_id: applicationId
            });
        }
    }, [applicationId, isTopup]);

    const isInNewUploadDocsSplitTest = useSplitTests('new_docs_upload');
    const isUKCustomer = getCountry() === 'UK';

    const shouldSeeNewDocUploadsPage =
        isInNewUploadDocsSplitTest && isUKCustomer && isRepeatCustomer;

    return shouldSeeNewDocUploadsPage ? (
        <NewDocUploads />
    ) : (
        <div>
            <ApprovalRequirements
                submitFailed={submitFailed}
                flexiLoanDocumentRequirements={flexiLoanDocumentRequirements}
            />
        </div>
    );
};
